'use client'
import React, { memo, useEffect, useRef, useState } from 'react'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'


interface GLBThumbnailProps {
	src: string
	className?: string
	style?: React.CSSProperties
}


const GLBThumbnail: React.FC<GLBThumbnailProps> = ({ src, className = '', style = {} }) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const [thumb, setThumb] = useState<string | null>(null)
	const [loaded, setLoaded] = useState(false)

	useEffect(() => {
		let disposed = false
		let renderer: any, scene: any, camera: any

		;(async () => {
			const THREE = await import('three')
			const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')

			scene = new THREE.Scene()
			camera = new THREE.PerspectiveCamera(35, 1, 0.1, 1000)
			camera.position.set(1, 1, 1)

			renderer = new THREE.WebGLRenderer({
				canvas: canvasRef.current!,
				alpha: true,
				antialias: true,
				preserveDrawingBuffer: true,
			})
			renderer.setSize(300, 300, false)
			renderer.outputColorSpace = THREE.SRGBColorSpace

			scene.add(new THREE.AmbientLight(0xffffff, 0.9))
			const dir = new THREE.DirectionalLight(0xffffff, 0.8)
			dir.position.set(2, 3, 4)
			scene.add(dir)

			const loader = new GLTFLoader();
			const dracoLoader = new DRACOLoader()
				.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
				.setDecoderConfig({ type: 'wasm' });
			loader.setDRACOLoader(dracoLoader);


			loader.load(
				src,
				(gltf: GLTF) => {
					if (disposed) return
					const root = gltf.scene
					scene.add(root)
					// center & fit
					const box = new THREE.Box3().setFromObject(root)
					const size = box.getSize(new THREE.Vector3())
					const center = box.getCenter(new THREE.Vector3())
					root.position.sub(center)
					const maxDim = Math.max(size.x, size.y, size.z) || 1
					const tight = 0.7
					const dist = maxDim / (2 * Math.tan((Math.PI * camera.fov) / 360))
					camera.position.set(dist * tight, dist * tight, dist * tight)
					camera.lookAt(0, 0, 0)

					// render once textures are ready
					requestAnimationFrame(() => {
						renderer.render(scene, camera)
						setThumb(renderer.domElement.toDataURL('image/png'))
						setLoaded(true)
					})
				},
				undefined,
				() => setLoaded(true),
			)
		})()

		return () => {
			disposed = true
			try {
				renderer?.dispose?.()
				scene?.traverse?.((o: any) => {
					if (o.isMesh) {
						o.geometry?.dispose?.()
						if (o.material) {
							const mats = Array.isArray(o.material) ? o.material : [o.material]
							mats.forEach((m: any) => {
								for (const k in m) m[k]?.isTexture && m[k].dispose?.()
								m.dispose?.()
							})
						}
					}
				})
			} catch {
				// ignore
			}
		}
	}, [src])

	return (
		<div
			className={className}
			style={{
				position: 'relative',
				width: '100%',
				height: '100%',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				overflow: 'hidden',
				...style,
			}}
		>
			<canvas ref={canvasRef} style={{ display: 'none' }} />
			<img
				src={'/assets/images/Loading.gif'}
				alt="Loading"
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					opacity: loaded && thumb ? 0 : 1,
					transition: 'opacity .3s',
				}}
			/>
			{thumb && (
				<img
					src={thumb}
					alt="GLB preview"
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'contain',
						opacity: loaded ? 1 : 0,
						transition: 'opacity .3s',
					}}
				/>
			)}
		</div>
	)
}

GLBThumbnail.displayName = 'GLBThumbnail'
export default memo(GLBThumbnail, (p, n) => p.src === n.src)